import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import {
  ApiError,
  SobeysReport,
  SobeysReportRow,
  SobeysStatus,
  clearSobeysMatch,
  getSobeysReport,
  linkSobeysMatch,
  rejectSobeysMatch,
} from '../../api';
import Price from '../widgets/Price';

// Sobeys price check. Every row is a front-room item; the numbers on
// the right are what Sobeys Bridgewater charges for the same product,
// as recorded by the standalone `backroom-sobeys` crawler. Admin-gated:
// employees already see wholesale cost elsewhere in the back office.
//
// Two jobs on one page: read the table (worst undercut first), and work
// the match queue — items the crawler could not pair by UPC get their
// name-search candidates listed with Link / Not at Sobeys buttons.

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; report: SobeysReport };

type Filter = 'all' | SobeysStatus;

const STATUS_META: Record<
  SobeysStatus,
  { label: string; color: 'error' | 'info' | 'success' | 'warning' | 'default' }
> = {
  under_cost: { label: 'Under our cost', color: 'error' },
  on_sale: { label: 'On sale', color: 'info' },
  ok: { label: 'OK', color: 'success' },
  unmatched: { label: 'Unmatched', color: 'warning' },
  rejected: { label: 'Not at Sobeys', color: 'default' },
  stale: { label: 'Stale', color: 'default' },
};

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'under_cost', label: 'Under our cost' },
  { key: 'on_sale', label: 'On sale' },
  { key: 'unmatched', label: 'Unmatched' },
  { key: 'ok', label: 'OK' },
  { key: 'stale', label: 'Stale' },
  { key: 'rejected', label: 'Not at Sobeys' },
];

const RETAILER_LABELS: Record<string, string> = {
  sobeys: 'Sobeys',
  superstore: 'Superstore',
};

function retailerLabel(r: string): string {
  return RETAILER_LABELS[r] ?? r;
}

const CAD = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
});

function money(cents: number | null | undefined): string {
  return cents == null ? '–' : CAD.format(cents / 100);
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function relative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.round(ms / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function isValidFilter(s: string | null): s is Filter {
  return !!s && FILTERS.some((f) => f.key === s);
}

const SobeysPricesReportPage = () => {
  const { idToken } = useAuth();
  const location = useLocation();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [filter, setFilter] = useState<Filter>(() => {
    const q = new URLSearchParams(location.search).get('status');
    return isValidFilter(q) ? q : 'all';
  });
  const [retailer, setRetailer] = useState<string | undefined>(
    () => new URLSearchParams(location.search).get('retailer') ?? undefined,
  );
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [manualArticle, setManualArticle] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    if (!idToken) return;
    getSobeysReport(idToken, retailer)
      .then((report) => setState({ kind: 'ready', report }))
      .catch((e) => {
        const message =
          e instanceof ApiError && e.status === 403
            ? 'Your account is not permitted to view this report.'
            : e instanceof Error
              ? e.message
              : String(e);
        setState({ kind: 'error', message });
      });
  }, [idToken, retailer]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (state.kind !== 'ready') return [];
    return filter === 'all'
      ? state.report.rows
      : state.report.rows.filter((r) => r.status === filter);
  }, [state, filter]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = {
      all: 0, under_cost: 0, on_sale: 0, ok: 0, unmatched: 0, rejected: 0, stale: 0,
    };
    if (state.kind === 'ready') {
      for (const r of state.report.rows) {
        c.all += 1;
        c[r.status] += 1;
      }
    }
    return c;
  }, [state]);

  const act = async (row: SobeysReportRow, fn: () => Promise<void>) => {
    if (!idToken) return;
    setBusyItem(row.item_id);
    setActionError(null);
    try {
      await fn();
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyItem(null);
    }
  };

  const current = state.kind === 'ready' ? state.report.retailer : retailer ?? 'sobeys';
  const link = (row: SobeysReportRow, article: string) =>
    act(row, () => linkSobeysMatch(idToken!, row.tenant, row.item_id, article, current));
  const reject = (row: SobeysReportRow) =>
    act(row, () => rejectSobeysMatch(idToken!, row.tenant, row.item_id, current));
  const clear = (row: SobeysReportRow) =>
    act(row, () => clearSobeysMatch(idToken!, row.tenant, row.item_id, current));

  return (
    <Stack spacing={3}>
      <Breadcrumbs aria-label="report breadcrumb">
        <MuiLink
          component={Link}
          to="/reports"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Reports
        </MuiLink>
        <Typography sx={{ color: '#1f2937', fontWeight: 500 }}>
          Competitor price check
        </Typography>
      </Breadcrumbs>

      {state.kind === 'ready' && state.report.retailers.length > 0 && (
        <Stack direction="row" spacing={1}>
          {state.report.retailers.map((r) => (
            <Chip
              key={r}
              label={retailerLabel(r)}
              color={r === state.report.retailer ? 'primary' : 'default'}
              variant={r === state.report.retailer ? 'filled' : 'outlined'}
              onClick={() => {
                setRetailer(r);
                setState({ kind: 'loading' });
              }}
            />
          ))}
        </Stack>
      )}

      <Box>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          What {retailerLabel(current)} in Bridgewater charges for each
          front-room item, against our wholesale cost. <strong>Effective</strong> is what a customer
          pays there today: the sale price while a sale runs, otherwise
          regular, per unit; <strong>×2</strong> beside a sale means you must
          buy that many to get it. Sorted worst-first by effective minus cost. Items the
          crawler could not pair by UPC are <em>Unmatched</em>; pick the
          right candidate or mark them not sold at Sobeys.
        </Typography>
        {state.kind === 'ready' && (
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
            {retailerLabel(state.report.retailer)}
            {state.report.store_id ? ` store ${state.report.store_id}` : ''}. Last crawl:{' '}
            {state.report.last_run
              ? `${relative(state.report.last_run.started_at)} (${state.report.last_run.outcome ?? 'running'}, ${state.report.last_run.requests_made} requests, ${state.report.last_run.items_matched} matched, ${state.report.last_run.items_unmatched} unmatched${state.report.last_run.last_error ? `, last error: ${state.report.last_run.last_error}` : ''})`
              : 'none recorded yet'}
            . Data older than {state.report.stale_after_days} days shows as Stale
            and is left out of the banners.
          </Typography>
        )}
      </Box>

      {state.kind === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {state.kind === 'error' && <Alert severity="error">{state.message}</Alert>}
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {state.kind === 'ready' && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {FILTERS.map((f) => (
              <Chip
                key={f.key}
                label={`${f.label} (${counts[f.key]})`}
                color={
                  f.key === 'all'
                    ? 'default'
                    : STATUS_META[f.key].color
                }
                variant={filter === f.key ? 'filled' : 'outlined'}
                onClick={() => setFilter(f.key)}
                size="small"
              />
            ))}
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Our cost</TableCell>
                  <TableCell align="right">Our price</TableCell>
                  <TableCell align="right">Their regular</TableCell>
                  <TableCell align="right">Their sale</TableCell>
                  <TableCell align="right">Effective</TableCell>
                  <TableCell align="right">vs cost</TableCell>
                  <TableCell>Their product / candidates</TableCell>
                  <TableCell>Seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ color: '#94a3b8', textAlign: 'center', py: 4 }}>
                      Nothing here.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => {
                  const meta = STATUS_META[row.status];
                  const delta =
                    row.effective_cents != null && row.cost_cents != null
                      ? row.effective_cents - row.cost_cents
                      : null;
                  const busy = busyItem === row.item_id;
                  return (
                    <TableRow
                      key={row.item_id}
                      hover
                      sx={row.status === 'under_cost' ? { backgroundColor: '#fff1f2' } : undefined}
                    >
                      <TableCell>
                        <Chip label={meta.label} color={meta.color} size="small" />
                        {row.sobeys?.has_unparsed_promo && (
                          <Tooltip title="Sobeys has a promotion on this item whose shape the crawler doesn't parse yet (e.g. 2 for $5). Check the site.">
                            <Chip label="promo?" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                        {row.sobeys?.in_stock === false && (
                          <Chip label="out of stock" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {row.upc ?? 'no UPC'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><Price price={row.cost_cents} showZero /></TableCell>
                      <TableCell align="right"><Price price={row.price_cents} showZero /></TableCell>
                      <TableCell align="right">{money(row.sobeys?.regular_cents)}</TableCell>
                      <TableCell align="right">
                        {row.sobeys?.sale_cents != null ? (
                          <>
                            {money(row.sobeys.sale_cents)}
                            {(row.sobeys.sale_min_qty ?? 1) > 1 && (
                              <Tooltip title={row.sobeys.sale_label ?? ''}>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{ ml: 0.5, color: '#b45309', fontWeight: 600 }}
                                >
                                  ×{row.sobeys.sale_min_qty}
                                </Typography>
                              </Tooltip>
                            )}
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                              {row.sobeys.sale_label &&
                              (row.sobeys.sale_min_qty ?? 1) <= 1 &&
                              row.sobeys.sale_label !== 'Customer Price'
                                ? `${row.sobeys.sale_label} · `
                                : ''}
                              ends {shortDate(row.sobeys.sale_ends_at)}
                            </Typography>
                          </>
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {money(row.effective_cents)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: delta != null && delta < 0 ? '#b91c1c' : '#64748b' }}
                      >
                        {delta == null ? '–' : `${delta < 0 ? '−' : '+'}${money(Math.abs(delta))}`}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <MatchCell
                          row={row}
                          busy={busy}
                          manual={manualArticle[row.item_id] ?? ''}
                          onManualChange={(v) =>
                            setManualArticle((m) => ({ ...m, [row.item_id]: v }))
                          }
                          onLink={(a) => link(row, a)}
                          onReject={() => reject(row)}
                          onClear={() => clear(row)}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: '#64748b' }}>
                        {relative(row.sobeys?.last_seen_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
};

/** The Sobeys side of a row: the linked product, or the candidate list
 *  with Link buttons, or the rejected marker — each with its undo. */
const MatchCell = ({
  row,
  busy,
  manual,
  onManualChange,
  onLink,
  onReject,
  onClear,
}: {
  row: SobeysReportRow;
  busy: boolean;
  manual: string;
  onManualChange: (v: string) => void;
  onLink: (article: string) => void;
  onReject: () => void;
  onClear: () => void;
}) => {
  const m = row.match;
  if (m.rejected) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          marked not sold here
        </Typography>
        <Button size="small" disabled={busy} onClick={onClear}>
          undo
        </Button>
      </Stack>
    );
  }
  if (m.confirmed && m.article_number) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>{m.their_name ?? m.article_number}</Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            #{m.article_number} · {m.method === 'upc' ? 'UPC match' : m.method === 'manual' ? 'linked by hand' : m.method}
          </Typography>
        </Box>
        <Tooltip title="Forget this link; the next crawl searches again">
          <Button size="small" disabled={busy} onClick={onClear}>
            unlink
          </Button>
        </Tooltip>
      </Stack>
    );
  }
  return (
    <Stack spacing={0.5}>
      {m.candidates.length === 0 && (
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          no candidates found
        </Typography>
      )}
      {m.candidates.slice(0, 3).map((c) => (
        <Stack key={c.article_number} direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => onLink(c.article_number)}
          >
            Link
          </Button>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>{c.name}</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              #{c.article_number} · {money(c.price_cents)} · {Math.round(c.score * 100)}%
            </Typography>
          </Box>
        </Stack>
      ))}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          placeholder="article #"
          value={manual}
          onChange={(e) => onManualChange(e.target.value.replace(/\D/g, ''))}
          sx={{ width: 120 }}
          inputProps={{ inputMode: 'numeric' }}
        />
        <Button
          size="small"
          disabled={busy || manual.length === 0}
          onClick={() => onLink(manual)}
        >
          Link
        </Button>
        <Button size="small" color="inherit" disabled={busy} onClick={onReject}>
          Not sold here
        </Button>
      </Stack>
    </Stack>
  );
};

export default SobeysPricesReportPage;
